import React, { useEffect, useState } from 'react';
import { apiClient } from '../../shared/services/api';
import './SuccessStoryStep.css';

/**
 * Generates an auto-quote when the user doesn't provide one.
 * Exported for testability.
 */
export function generateAutoQuote(
  companyName: string,
  referrerName: string | null
): string {
  if (referrerName) {
    return `Got referred to ${companyName} through ${referrerName} on ReffConnect.`;
  }
  return `Just landed a role at ${companyName} through ReffConnect.`;
}

export interface SuccessStoryPayload {
  referrer_id: string | null;
  role_title: string;
  quote: string | null;
  auto_generated_quote: string;
}

interface SuccessStoryStepProps {
  upgradeRequestId: number;
  companyName: string;
  onSubmit: (payload: SuccessStoryPayload) => Promise<void>;
  onSkip: () => void;
  submitting: boolean;
}

export function SuccessStoryStep({
  upgradeRequestId,
  companyName,
  onSubmit,
  onSkip,
  submitting,
}: SuccessStoryStepProps) {
  const [referrers, setReferrers] = useState<{ id: string; name: string }[]>([]);
  const [selectedReferrerId, setSelectedReferrerId] = useState<string>('');
  const [roleTitle, setRoleTitle] = useState('');
  const [quote, setQuote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.getCompletedBookingReferrers();
        if (res.success && res.data) {
          setReferrers(res.data);
        }
      } catch {
        // If it fails, the dropdown is just omitted
      }
    })();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedRole = roleTitle.trim();
    if (!trimmedRole) {
      setError('Please enter the role you landed.');
      return;
    }

    const selectedReferrer = referrers.find(r => r.id === selectedReferrerId) ?? null;
    const autoQuote = generateAutoQuote(companyName, selectedReferrer?.name ?? null);

    await onSubmit({
      referrer_id: selectedReferrerId || null,
      role_title: trimmedRole,
      quote: quote.trim() || null,
      auto_generated_quote: autoQuote,
    });
  };

  return (
    <form
      className="sss__form"
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby="sss-title"
    >
      <div className="hired-modal__scroll">
        <div className="hired-modal__head">
          <div>
            <span className="hired-modal__eyebrow">One more thing</span>
            <h2 id="sss-title" className="hired-modal__title">
              Share your story 🎉
            </h2>
          </div>
        </div>

        <p className="hired-modal__subtext">
          Help others discover who can refer them. This is optional — you can skip anytime.
        </p>

        <div className="hired-modal__fields">
          {referrers.length > 0 && (
            <div className="hired-modal__field">
              <label className="hired-modal__label" htmlFor="sss-referrer">
                Who referred you? <span className="sss__optional">(optional)</span>
              </label>
              <select
                id="sss-referrer"
                className="hired-modal__input"
                value={selectedReferrerId}
                onChange={e => setSelectedReferrerId(e.target.value)}
                disabled={submitting}
              >
                <option value="">— Not applicable —</option>
                {referrers.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="hired-modal__field">
            <label className="hired-modal__label" htmlFor="sss-role">
              Role you landed <span className="hired-modal__required">*</span>
            </label>
            <input
              id="sss-role"
              type="text"
              className="hired-modal__input"
              placeholder="e.g. Software Engineer, Product Manager"
              value={roleTitle}
              maxLength={120}
              onChange={e => setRoleTitle(e.target.value)}
              disabled={submitting}
              autoComplete="off"
            />
          </div>

          <div className="hired-modal__field">
            <label className="hired-modal__label" htmlFor="sss-quote">
              Share a short quote <span className="sss__optional">(optional)</span>
            </label>
            <textarea
              id="sss-quote"
              className="hired-modal__input sss__textarea"
              placeholder="e.g. ReffConnect helped me land my dream role in just 3 weeks!"
              value={quote}
              maxLength={300}
              rows={3}
              onChange={e => setQuote(e.target.value)}
              disabled={submitting}
            />
            <span className="sss__char-count">{quote.length}/300</span>
          </div>
        </div>

        {error && (
          <p className="hired-modal__error" role="alert">
            {error}
          </p>
        )}
      </div>

      <div className="hired-modal__actions">
        <button
          type="button"
          className="hired-modal__btn hired-modal__btn--ghost"
          onClick={onSkip}
          disabled={submitting}
        >
          Skip
        </button>
        <button
          type="submit"
          className="hired-modal__btn hired-modal__btn--primary"
          disabled={submitting}
        >
          {submitting ? 'Saving...' : 'Share my story'}
          {!submitting && <span className="hired-modal__btn-shine" aria-hidden />}
        </button>
      </div>
    </form>
  );
}
